import { api } from './api';

export interface LadderUser {
  user: {
    _id: string;
    username: string;
    profile?: {
      firstName?: string;
      lastName?: string;
      avatar?: string;
    };
  };
  position: number;
  previousPosition?: number;
  movement: 'up' | 'down' | 'same' | 'new';
  stats: {
    totalOrders: number;
    completedOrders: number;
    totalRevenue: number;
    totalVolume: number;
  };
  isWhatsAppQualified: boolean;
  lastUpdated: string;
}

export interface UserPosition {
  position: number | null;
  movement: 'up' | 'down' | 'same' | 'new';
  stats: {
    totalOrders: number;
    completedOrders: number;
    totalRevenue: number;
    totalVolume: number;
  };
  isWhatsAppQualified: boolean;
  totalParticipants: number;
  message: string;
}

export interface WhatsAppStatus {
  isQualified: boolean;
  isInWhatsAppCommunity: boolean;
  message: string;
}

export interface LadderHistory {
  weekIdentifier: string;
  weekStart: string;
  weekEnd: string;
  position: number | null;
  stats: {
    totalOrders: number;
    completedOrders: number;
    totalRevenue: number;
    totalVolume: number;
  };
  movement: 'up' | 'down' | 'same' | 'new';
  isWinner: boolean;
}

class SalesLadderService {
  /**
   * Get current sales ladder
   */
  async getCurrentLadder(): Promise<{ success: boolean; data: any }> {
    try {
      const response = await api.get('/sales-ladder/current');
      return response.data;
    } catch (error: any) {

      throw error;
    }
  }

  /**
   * Get user's position on ladder
   */
  async getMyPosition(): Promise<{ success: boolean; data: UserPosition }> {
    try {
      const response = await api.get('/sales-ladder/my-position');
      return response.data;
    } catch (error: any) {
      globalThis.console.error('[SalesLadder] getMyPosition failed:', error?.response?.status || error?.message || error);

      // Return fallback data instead of throwing
      return {
        success: false,
        data: {
          position: null,
          movement: 'new',
          stats: { totalOrders: 0, completedOrders: 0, totalRevenue: 0, totalVolume: 0 },
          isWhatsAppQualified: false,
          totalParticipants: 0,
          message: 'Unable to load position data'
        }
      };
    }
  }

  /**
   * Get top performers
   */
  async getTopPerformers(limit: number = 10): Promise<{ success: boolean; data: LadderUser[] }> {
    try {
      const response = await api.get(`/sales-ladder/top-performers?limit=${limit}`);
      return response.data;
    } catch (error: any) {

      // Return fallback data instead of throwing
      return {
        success: false,
        data: []
      };
    }
  }

  /**
   * Get current winners (top 3)
   */
  async getCurrentWinners(): Promise<{ success: boolean; data: LadderUser[] }> {
    try {
      const response = await api.get('/sales-ladder/winners');
      return response.data;
    } catch (error: any) {

      return {
        success: false,
        data: []
      };
    }
  }

  /**
   * Get WhatsApp status
   */
  async getWhatsAppStatus(): Promise<{ success: boolean; data: WhatsAppStatus }> {
    try {
      const response = await api.get('/sales-ladder/whatsapp-status');
      return response.data;
    } catch (error: any) {

      // Return fallback data instead of throwing
      return {
        success: false,
        data: {
          isQualified: false,
          isInWhatsAppCommunity: false,
          message: 'Unable to check WhatsApp status'
        }
      };
    }
  }

  /**
   * Get ladder history
   */
  async getLadderHistory(limit: number = 10): Promise<{ success: boolean; data: LadderHistory[] }> {
    try {
      const response = await api.get(`/sales-ladder/my-history?limit=${limit}`);
      return response.data;
    } catch (error: any) {

      return {
        success: false,
        data: []
      };
    }
  }

  /**
   * Join WhatsApp community
   */
  async joinWhatsAppCommunity(phoneNumber: string): Promise<{ success: boolean; data: any }> {
    // Backend does not expose a join endpoint; joining is done via WhatsApp group link.
    // Keep method for backward compatibility.
    return {
      success: false,
      data: {
        message: 'Join via WhatsApp group link'
      }
    };
  }

  /**
   * Get leaderboard for specific period
   */
  async getLeaderboard(period: string = 'current', limit: number = 50): Promise<{ success: boolean; data: LadderUser[] }> {
    try {
      // No dedicated leaderboard endpoint in backend; use top performers instead.
      const response = await api.get(`/sales-ladder/top-performers?limit=${limit}`);
      return response.data;
    } catch (error: any) {

      return {
        success: false,
        data: []
      };
    }
  }

  /**
   * Get user statistics
   */
  async getUserStats(): Promise<{ success: boolean; data: any }> {
    try {
      // Backend exposes /my-position which includes user stats
      const response = await api.get('/sales-ladder/my-position');
      return response.data;
    } catch (error: any) {

      return {
        success: false,
        data: {
          totalOrders: 0,
          completedOrders: 0,
          totalRevenue: 0,
          totalVolume: 0,
          currentRank: null,
          bestRank: null
        }
      };
    }
  }
}

export const salesLadderService = new SalesLadderService();

export interface LoyaltyPointsSummary {
  availablePoints: number;
  estimatedWalletCredit: number;
  pendingRedemptionPoints: number;
  pendingRedemptionCredit: number;
  pointsToGhsRate: number;
}

export interface LoyaltyRedemptionRequest {
  _id: string;
  user_id: string;
  points: number;
  creditAmount: number;
  status: 'pending' | 'approved' | 'rejected';
  allocations: Array<{ bucket: string; points: number }>;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  rejectionReason?: string | null;
  walletTransactionId?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

class LoyaltyPointsService {
  async getSummary(): Promise<{ success: boolean; data: LoyaltyPointsSummary }> {
    const response = await api.get('/loyalty-points/summary');
    return response.data;
  }

  async getMyRedemptions(limit: number = 20): Promise<{ success: boolean; data: LoyaltyRedemptionRequest[] }> {
    const response = await api.get(`/loyalty-points/my-redemptions?limit=${limit}`);
    return response.data;
  }

  async redeem(points: number): Promise<{ success: boolean; message?: string; data: LoyaltyRedemptionRequest }> {
    const response = await api.post('/loyalty-points/redeem', { points });
    return response.data;
  }
}

export const loyaltyPointsService = new LoyaltyPointsService();
